
import React from 'react';
import { FileSearch, Database, Zap, ServerCog, Bot, Terminal } from 'lucide-react';
import AnimatedCard from '../AnimatedCard';
import SectionHeader from '../common/SectionHeader';

const CustomSolutionsExamples = () => {
  const examples = [
    {
      title: "Automated Workflows for Core Legal Operations",
      description: "A few examples of what AI customization can do for your practice.",
      background: "bg-[#222538]",
      examples: [
        { 
          title: "Custom RAG-Enabled Chatbots & Assistants",
          icon: <FileSearch className="h-8 w-8 text-blue-400" />,
          code: "AI.load(firmData).createChatbot({ vectorStore: true })",
          techSpecs: ["Vector DB", "LLM Integration", "API Access"]
        },
        { 
          title: "Personalized Lead Generation Flows",
          icon: <Database className="h-8 w-8 text-purple-400" />,
          code: "LeadGen.automate({ source: 'website', followUp: true })",
          techSpecs: ["CRM Integration", "Auto Email", "Scoring Engine"] 
        },
        { 
          title: "Automated Blog and Content Creation",
          icon: <Zap className="h-8 w-8 text-amber-400" />,
          code: "Content.generate({ topic: 'legal', SEO: true })",
          techSpecs: ["NLP Processor", "SEO Optimizer", "Publishing API"]
        }
      ]
    },
    {
      title: "Have a Challenge in Mind? Let's Build It Together.",
      description: "Every firm is different. If you already see an opportunity to automate or enhance your operations, we'll collaborate with you to design a custom system that fits your specific goals. Bring your ideas — or just your pain points — and we'll help you map the path forward.",
      background: "bg-[#2C2A3A]",
      commandLine: true
    }
  ];

  return (
    <section className="py-12">
      <div className="container mx-auto px-6">
        <SectionHeader 
          title="with Custom Solutions"
          highlightedText="See What's Possible"
          description="From helping you earn more business to getting the work done more efficiently, the question isn't if you need a custom solution, it's where and when you decide to start."
        />
        
        <div className="space-y-8">
          {examples.map((example, index) => (
            <AnimatedCard key={index} animation="fade-in" delay={index * 100} className={`${example.background} rounded-2xl shadow-2xl overflow-hidden`}>
              <div className="p-8 md:p-10 text-white">
                {/* Section Header */}
                <div className="flex items-center mb-6">
                  {index === 0 && <ServerCog className="h-6 w-6 text-blue-400 mr-3" />}
                  {index === 1 && <Bot className="h-6 w-6 text-purple-400 mr-3" />}
                  <h3 className="text-2xl md:text-3xl font-bold">{example.title}</h3>
                </div>
                
                <p className="text-lg text-gray-300 leading-relaxed">{example.description}</p>
                
                {example.examples && (
                  <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {example.examples.map((item, idx) => (
                      <div 
                        key={idx} 
                        className="bg-[#1A1D29] rounded-xl p-6 border border-white/10 hover:border-blue-500/40 transition-all relative overflow-hidden group"
                      >
                        {/* Technical grid background */}
                        <div className="absolute inset-0 opacity-5 bg-grid-pattern pointer-events-none"></div>
                        
                        <div className="flex flex-col items-center text-center relative z-10">
                          {/* Tech icon with glowing effect */}
                          <div className="mb-4 p-3 bg-[#252837] rounded-full group-hover:glow-blue transition-all">
                            {item.icon}
                          </div>
                          
                          <h4 className="font-medium text-xl text-white mb-4">{item.title}</h4>
                          
                          {/* Code snippet */}
                          <div className="bg-[#12141D] w-full p-2 rounded-md font-mono text-xs text-green-400 mb-4 text-left overflow-x-auto">
                            <span className="text-blue-400">// </span>
                            {item.code}
                          </div>
                          
                          {/* Tech specs list */}
                          <div className="flex flex-wrap justify-center gap-2 mt-2">
                            {item.techSpecs.map((spec, i) => (
                              <span key={i} className="px-2 py-1 bg-white/5 rounded-full text-xs font-mono text-blue-300">
                                {spec}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {example.commandLine && (
                  <div className="mt-8 bg-[#1A1D29] rounded-lg p-4 font-mono text-sm relative overflow-hidden">
                    <div className="flex items-center mb-2">
                      <div className="w-3 h-3 rounded-full bg-red-500 mx-1"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500 mx-1"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500 mx-1"></div>
                      <span className="ml-2 text-gray-400 text-xs">terminal</span>
                    </div>
                    <div className="terminal-text flex">
                      <Terminal className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                      <span className="text-green-400">docketlabs</span>
                      <span className="text-blue-400 mx-2">~</span>
                      <span className="text-white">let's build your custom AI solution</span>
                      <span className="text-white animate-pulse ml-1">|</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Decorative tech pattern */}
              <div className="h-1 w-full bg-gradient-to-r from-purple-400/30 via-blue-500/30 to-cyan-400/30"></div>
            </AnimatedCard>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CustomSolutionsExamples;
